/****************************************************************************
 * 한국어 데이터 처리를 위한 정의
 * UI 표시를 위한 정의는 desktop.const.ts 에서 처리 (책상형 웹) 모바일 UI는 client.const.ts 에서 정의
 *
 * Created by 박주형 on 2023-04-25
 ****************************************************************************/


export const WORD_SEPARATOR_NUM = '|';    // 뜻이나 예문의 번호로 구분된 항목을 나누는 separator
export const WORD_SEPARATOR_LINE = '/';    // 뜻이나 예문의 문장을 나누는 separator, SEPARATOR_NUM 내부에 존재함.

export const DERIVATIVE_SEPARATOR = '|';

/******************************************************************************
 * 자소 정의
 */


export type JASO = string;  // 메인 자소 + 서브 자소(=한글 자소)


export type MAIN_JASO = 'ㄱ' | 'ㄴ' | 'ㄷ' | 'ㄹ' | 'ㅁ' | 'ㅂ' | 'ㅅ' | 'ㅇ' | 'ㅈ' | 'ㅊ' | 'ㅋ' | 'ㅌ' | 'ㅍ' | 'ㅎ';

export const MAIN_JASO_LIST: Array<MAIN_JASO> = [
  'ㄱ', 'ㄴ', 'ㄷ', 'ㄹ', 'ㅁ', 'ㅂ', 'ㅅ', 'ㅇ', 'ㅈ', 'ㅊ', 'ㅋ', 'ㅌ', 'ㅍ', 'ㅎ',
];

export function isMainJASO(char: string): char is MAIN_JASO {
  return MAIN_JASO_LIST.includes(char as MAIN_JASO);
}


const SINGLE_INDEX_LIST: Array<[MAIN_JASO, string[] | null]> = [
  ['ㄱ', ['가', '각', '강', '거', '겁', '경', '고', '곰', '관', '구', '국', '그', '기', 'ㄲ']],
  ['ㄴ', ['나', '내', '너', '노', '누', '느', '니']],
  ['ㄷ', ['다', '대', '더', '도', '돈', '두', '드', '디', '따', '떠', '또', '뚜', '뜨', '띠']],
  ['ㄹ', null],
  ['ㅁ', ['마', '말', '망', '머', '멸', '모', '목', '무', '묵', '물', '미', '민', '밀']],
  ['ㅂ', ['바', '반', '발', '배', '버', '벽', '보', '본', '부', '불', '비', '빈', 'ㅃ']],
  ['ㅅ', ['사', '산', '새', '서', '선', '성', '소', '속', '수', '숙', '스', '시', '신', 'ㅆ']],
  ['ㅇ', ['아', '알', '야', '어', '업', '연', '영', '오', '와', '우', '원', '유', '으', '이']],
  ['ㅈ', ['자', '작', '장', '저', '절', '정', '조', '졸', '주', '준', '즈', '지', '진', 'ㅉ']],
  ['ㅊ', ['차', '참', '처', '철', '초', '총', '최', '충', '츠', '치']],
  ['ㅋ', ['카', '커', '코', '쿠', '크', '키']],
  ['ㅌ', ['타', '태', '터', '토', '투', '트', '티']],
  ['ㅍ', ['파', '판', '팔', '패', '퍼', '펴', '평', '포', '표', '푸', '풍', '피']],
  ['ㅎ', ['하', '항', '해', '허', '혀', '호', '화', '활', '효', '후', '휴', '흐', '히']],
];

/**
 * SINGLE_INDEX_LIST의 데이터를 이용하여 SUB_INDEX_MAP과 같은 결과를 생성한다.
 * @param list 메인 자소와 하위 문자열 배열의 튜플 리스트
 * @returns 메인 자소와 변환된 하위 문자열 배열의 튜플 리스트
 */
function generateSubIndexMap(
  list: Array<[MAIN_JASO, string[] | null]>
): Array<[MAIN_JASO, Array<string> | null]> {
  const result: Array<[MAIN_JASO, Array<string> | null]> = [];

  for (let i = 0; i < list.length; i++) {
    const [currentJaso, subArray] = list[i];

    // 1) subArray가 null이면 그대로 null로 처리
    if (!subArray) {
      result.push([currentJaso, null]);
      continue;
    }

    // 2) 인접한 두 단어를 합쳐 새 배열 생성
    const pairs: string[] = [];
    for (let j = 0; j < subArray.length - 1; j++) {
      pairs.push(subArray[j] + subArray[j + 1]);
    }

    // 3) 마지막 요소와 다음 자소(또는 '힣')를 이어 붙이는 브리지 pair 생성
    if (i < list.length - 1) {
      const [nextJaso] = list[i + 1];
      pairs.push(subArray[subArray.length - 1] + nextJaso);
    } else {
      // 마지막 자소의 경우 '힣'을 이어 붙임
      pairs.push(subArray[subArray.length - 1] + '힣');
    }

    result.push([currentJaso, pairs]);
  }

  return result;
}


// Map 생성
export const SUB_INDEX_MAP: Map<MAIN_JASO, Array<string> | null> = new Map(
  generateSubIndexMap(SINGLE_INDEX_LIST)
);

/**
 * '내'넣으면 속한 자소와 목록을 찾아서 반환한다.
 * @param value
 */
export function findMainJasoList(value: string): [MAIN_JASO | null, Array<string>] {
  for (let [jaso, subList] of SUB_INDEX_MAP.entries()) {
    if (subList?.includes(value)) {
      return [jaso, subList];
    }
  }
  return [null, MAIN_JASO_LIST];
}

export function GetJasoFromString(str: string): JASO | null {
  if ('가' <= str && str < '나')      { return 'ㄱ'; }
  else if ('나' <= str && str < '다') { return 'ㄴ'; }
  else if ('다' <= str && str < '라') { return 'ㄷ'; }
  else if ('라' <= str && str < '마') { return 'ㄹ'; }
  else if ('마' <= str && str < '바') { return 'ㅁ'; }
  else if ('바' <= str && str < '사') { return 'ㅂ'; }
  else if ('사' <= str && str < '아') { return 'ㅅ'; }
  else if ('아' <= str && str < '자') { return 'ㅇ'; }
  else if ('자' <= str && str < '차') { return 'ㅈ'; }
  else if ('차' <= str && str < '카') { return 'ㅊ'; }
  else if ('카' <= str && str < '타') { return 'ㅋ'; }
  else if ('타' <= str && str < '파') { return 'ㅌ'; }
  else if ('파' <= str && str < '하') { return 'ㅍ'; }
  else if ('하' <= str && str <= '힣') { return 'ㅎ'; }
  return null;
}


/*****************************************************************************/

/**
 * 성조는 1~5까지 5단계
 * 0은 잘못된 값을 의미
 */
export const SEUNGJO_STEP = 5;
export const SEUNGJO_MIDDLE = 3;  // 성조 중간값 (현재 중간 앖은 화면에 표시 안함)
export const SEUNGJO_INVALID = 0;

export enum POOMSA {
  NONE,
  MYUNGSA,
  HYUNGYONGSA,
  KWANHYUNGSA,
  DONGSA,
  TASONGSA,
  JADONGSA,
  BOOSA,
  JOSA,
  JEOPSA,
  SUSA,
  GAMTANSA,
  JUNMAL,
  BOJODONGSA,
  BOJOHYUNG,
  UIJONMYEONGSA,
  INCHINGDAEMYUNGSA,
  DAEMYUNGSA,
  EOMI,
  SANGTAEBYUNSA, //   상태 변사
}

/**
 * 품사 표시 문자열 enum POOMSA 와 순서가 일치해야 함
 */
export const POOMSA_DISP_LISP: Array<string> = [
  '',
  '명',
  '형',
  '관',
  '동',
  '타',
  '자',
  '부',
  '조',
  '접',
  '수',
  '감',
  '준',
  '보동',
  '보형',
  '의명',
  '인대',
  '대',
  '어',
  '상',
];

export const POOMSA_SYMBOL_MAP = new Map( [
  [ 'M', POOMSA.MYUNGSA ],
  [ 'H', POOMSA.HYUNGYONGSA ],
  [ 'K', POOMSA.KWANHYUNGSA ],
  [ 'D', POOMSA.DONGSA ],
  [ 'T', POOMSA.TASONGSA ],
  [ 'J', POOMSA.JADONGSA ],
  [ 'B', POOMSA.BOOSA ],
  [ 'C', POOMSA.JOSA ],
  [ 'A', POOMSA.JEOPSA ],
  [ 'S', POOMSA.SUSA ],
  [ 'E', POOMSA.GAMTANSA ],
  [ 'F', POOMSA.JUNMAL ],
  [ 'V', POOMSA.BOJODONGSA ],
  [ 'U', POOMSA.BOJOHYUNG ],
  [ 'W', POOMSA.UIJONMYEONGSA ],
  [ 'I', POOMSA.INCHINGDAEMYUNGSA ],
  [ 'G', POOMSA.DAEMYUNGSA ],
  [ 'L', POOMSA.EOMI ],
  [ 'N', POOMSA.SANGTAEBYUNSA ],
]);


export enum SASEUNG {
  NONE,
  PYUNG,  // 평
  SANG,   // 상
  GEO,    // 거
  IP,     // 입
  KUK,    // 국
  BUM,    // 범
}

// enum과 순서 맞춰야 함.
// 참고: 국, 범은 style.scss의 _saseung mixin에서 빈 문자열로 처리하여 화면에 표시되지 않음.
export const SASEUNG_DISP_LIST = [
  '',
  '평',
  '상',
  '거',
  '입',
  '국',
  '범',
];

export const SASEUNG_SYMBOL_LIST = [
  '',
  'p',
  's',
  'g',
  'i',
  'k',
  'b',
];

export const SASEUNG_SYMBOL_MAP = new Map([
  [ 'P', SASEUNG.PYUNG ],
  [ 'S', SASEUNG.SANG ],
  [ 'G', SASEUNG.GEO ],
  [ 'I', SASEUNG.IP ],
  [ 'K', SASEUNG.KUK ],
  [ 'B', SASEUNG.BUM ],
]);

export enum JANGDAN {
  NONE,
  JANGEUM,
  KKEUNEOIKGI,
  SPACE,
  DASH,
  UNDERBAR,
  RIGHT_ARROW,
  LEFT_ARROW,
}


// 표제어 상단에 아무것도 표시하지 않아야 하는 글자들
export const JANGDAN_TXT_MAP = new Map([
  [ ':', JANGDAN.JANGEUM ],
  [ '/', JANGDAN.KKEUNEOIKGI ],
  [ ' ', JANGDAN.SPACE ],
  [ '-', JANGDAN.DASH ],
  [ '_', JANGDAN.UNDERBAR ],
  [ '→', JANGDAN.RIGHT_ARROW ],
  [ '←', JANGDAN.LEFT_ARROW ],
]);


/****************************************************************************
 * 모두 우리말 (사투리) 정의
 ***************************************************************************/

export enum REGION {
  ALL,  // 전국

  GANGWON,
  GYEONGSANG,
  JEONLA,
  JEJU,
  CHUNGCHEONG,

  MAX,
};


/**
 * GetRegionName() 함수는 범용적으로 만든 함수
 * GetRegionText() 함수는 유사하지만 UI를 위해서 만든 함수.
 *                 GetRegionText()는 전체를 '전국'으로 표시하지만,
 *                 이 함수는 '전국'을 '전국'으로 표시하지 않고, '전국'을 제외한 지역만 표시함.
 *
 * @param {REGION} region - 지역 enum 값
 * @returns {string} 지역 이름
 */
export function GetRegionName(region: REGION): string | null {
  const mapRegion: Map<REGION, string> = new Map([
    [ REGION.ALL, '전국' ],
    [ REGION.GANGWON, '강원도' ],
    [ REGION.GYEONGSANG, '경상도' ],
    [ REGION.JEONLA, '전라도' ],
    [ REGION.JEJU, '제주도' ],
    [ REGION.CHUNGCHEONG, '충청도' ],
  ]);

  return mapRegion.get(region) ?? null;
}


/**
 * GetRegionByName() 함수는 범용적으로 만든 함수
 * GetRegionText() 함수는 유사하지만 UI를 위해서 만든 함수.
 *
 * @param {string} name - 지역 이름
 * @returns {REGION} 지역 enum 값
 */
export function GetRegionByName(name: string): REGION | null {
  const mapRegion: Map<string, REGION> = new Map([
    [ '전국', REGION.ALL ],
    [ '강원도', REGION.GANGWON ],
    [ '경상도', REGION.GYEONGSANG ],
    [ '전라도', REGION.JEONLA ],
    [ '제주도', REGION.JEJU ],
    [ '충청도', REGION.CHUNGCHEONG ],
  ]);

  return mapRegion.get(name) ?? null;
}


export type SUB_REGION_INFO = {
  region: REGION;
  texts: Array<string>;
};

export const SUB_REGION_LIST: Array<SUB_REGION_INFO> = [
  {
    region: REGION.GANGWON,
    texts: ['강릉', ],
  },
  {
    region: REGION.GYEONGSANG,
    texts: ['대구', '부산', ],
  },
  {
    region: REGION.JEONLA,
    texts: ['광주', ],
  },
  {
    region: REGION.JEJU,
    texts: ['제주시', ],
  },
  {
    region: REGION.CHUNGCHEONG,
    texts: ['대전', '제천', ],
  },
];

export function GetSubRegionByText(text: string): SUB_REGION_INFO | null {
  const idx = SUB_REGION_LIST.findIndex(subInfo => subInfo.texts.includes(text));
  return idx === -1 ? null : SUB_REGION_LIST[idx];
}



/******************************************************************************
 * 지역 정보 버전 2
 *****************************************************************************/

export type REGION2 = '강원' | '경남' | '경북' | '전남' | '전북' | '제주' | '충남' | '충북' | '전국';
export const REGION2_DEFAULT: REGION2 = '전국';


export function isRegion2(region: string): region is REGION2 {
  return region === '강원' ||
    region === '경남' ||
    region === '경북' ||
    region === '전남' ||
    region === '전북' ||
    region === '제주' ||
    region === '충남' ||
    region === '충북' ||
    region === REGION2_DEFAULT;
}


export type CITY2 = '전국' | '강릉' | '대구' | '부산' | '포항' | '하동' | '광주' | '전주' | '대전' | '서산' | '제천';
export const CITY2_DEFAULT: CITY2 = '전국';

export function isCity2(city2: CITY2): city2 is CITY2 {
return city2 === '강릉' ||
    city2 === '대구' ||
    city2 === '부산' ||
    city2 === '포항' ||
    city2 === '하동' ||
    city2 === '광주' ||
    city2 === '전주' ||
    city2 === '대전' ||
    city2 === '서산' ||
    city2 === '제천';
}

export function isCity2Type(city2: CITY2): city2 is CITY2 {
  return isCity2(city2) || city2 === CITY2_DEFAULT;
}


/******************************************************************************
 * 녹음자 정보
 *****************************************************************************/
export enum RECORDER_TYPE {
  RECORDER_A,
  RECORDER_B,
  RECORDER_C,
};


// RECORDER_TYPE과 순서가 동일해야햠.
const RECORD_TYPE_LIST: Array<string> = [
  'a',
  'b',
  'c',
];


export function getRecorderType(recorder: string): RECORDER_TYPE {
  const idx =  RECORD_TYPE_LIST.findIndex(type => type === recorder);
  if (idx === -1) {
    throw Error(`invalid recorder [${recorder}]`);
  }
  return idx;
}



